// Карта «Рядом с домом»: анкеты репетиторов и кружков (design/tasks/tutors-map.md).
//
// submitMapPartner — форма /tutors/join: капча → проверка → запись со
// status = 'pending' → письмо 3a партнёру (если указал почту) → строка в ленте
// «Уведомления» /admin (map-partner-notify.ts). Письма админу о новой анкете
// нет (19.10.2026). Ближайшее метро и slug считает сервер.
// Фото по желанию (map-photo.ts): проверяется до капчи, кладётся в приватный
// бакет ПОСЛЕ вставки строки, его сбой анкету не отменяет.
// listMapPartners / setMapPartnerStatus / removeMapPartnerPhoto — «Анкеты» в /admin; при
// публикации уходит письмо 3b. countUnseenMapPartners / markMapPartnersViewed —
// счётчик непросмотренных на вкладке (колонка admin_viewed_at, fail-open до наката).
//
// Точная точка партнёра в браузер не уходит: наружу — только представление
// map_partners_public (точка только у building, у travel/online координат нет).
import { createServerFn } from "@tanstack/react-start";
import { normalizePhoneAz } from "@/lib/phone-az";
import { z } from "zod";

import {
  DISTRICTS,
  categoryOf,
  inAzerbaijan,
  isDistrict,
  isFormat,
  isGrade,
  isSubjectOf,
} from "@/data/map-taxonomy";
import { normalizeInstagram } from "@/lib/instagram";
import { EmailField } from "@/lib/email-normalize";
import { inviteLink, normalizeRef, referrerFor } from "@/lib/partner-ref";
import { BAKU_CITY, cityLabel, isActiveCity, type MapCity } from "@/lib/map-cities";
import { ABSHERON_CITY, ABSHERON_PLACES, isDistrictOf, isMetro, placeByPin } from "@/lib/map-places";
import { loadMapCities } from "@/lib/map-cities.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { travelDistrictsFor, travelDistrictsMissing } from "@/lib/map-building-travel";
import { sendResendEmail } from "@/lib/email/resend-client.server";
import { publishedEmail, receivedEmail } from "@/lib/map-partner-emails";
import { countUnseen, feedTitle, isMissingColumn, isMissingViewedColumn } from "@/lib/map-partner-notify";
import { formHealthTags, joinStageHealth } from "@/lib/form-health";
import { UTM_COLUMNS, utmColumns } from "@/lib/utm";
import { notifyAdmin } from "@/lib/notifications.server";
import { MAP_PHOTO_BUCKET, MAP_PHOTO_MAX_B64, decodePhoto, objectKeyFromStored } from "@/lib/map-photo";
import { slugify } from "@/lib/partner-slug";
import { emailQrImageUrl } from "@/lib/partner-qr";
import { removePartnerPhotoAsAdmin, replacePartnerPhotoAsAdmin, savePartnerPhoto, type PhotoColumn, type PhotoStorage } from "@/lib/map-photo.server";
import { assertTurnstileOk, verifyTurnstile } from "@/lib/security/turnstile.server";
import { callerIp, hashIp } from "@/lib/request-ip.server";
import { readAll } from "@/lib/edu-read-all";
import { DUPLICATE_ERROR, findDuplicate, type DuplicateMatch, type PartnerContactRow } from "@/lib/partner-duplicate";
import { TEACH_LANGS_COLUMN, normalizeTeachLangs } from "@/lib/teach-langs";

const SITE = "https://yolutap.com";
// Адрес для ответов партнёру (reply_to писем 3a/3b).
const SUPPORT ="support@yolutap.com";
const FROM_ADDRESS = "Yolutap <notify@notify.yolutap.com>";

/* eslint-disable @typescript-eslint/no-explicit-any */
const partners = () => (supabaseAdmin.from as unknown as (t: string) => any)("map_partners");
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Ключ Google Maps для браузера — переменная воркера GOOGLE_MAPS_BROWSER_KEY,
 * в коде его нет. Ключ браузерный по природе: его защищает ограничение по
 * адресу сайта в Google Cloud. Нет ключа — форма работает с заглушкой карты.
 */
export const getMapsBrowserKey = createServerFn({ method: "GET" }).handler(async () => {
  const key = process.env.GOOGLE_MAPS_BROWSER_KEY;
  return { key: key && key.trim() ? key.trim() : null };
});

const Phone = z
  .string()
  .trim()
  .min(7)
  .max(40)
  .regex(/^[\d+()\-\s]+$/);

type CityInput = {
  address_mode: string;
  point: { lat: number; lng: number } | null;
  city?: string | null;
  district: string | null;
  travel_districts: string[];
};

/**
 * Город анкеты — СЧИТАЕТ СЕРВЕР, клиенту не верим (16.10.2026).
 *
 * У здания с пином город — по пину (placeByPin, src/lib/map-places.ts: границы
 * районов Баку, Абшерона и Сумгаита, иначе ближайший центр из map_cities;
 * до 26.10.2026 — контур Баку, захватывавший Хырдалан). Присланный формой city здесь не читается вовсе:
 * анкета ayten-memmedova-103653 с пином в Кюрдамире сохранилась бакинской с
 * районом Yasamal и метро Xocəsən.
 *
 * Выезд и здание без пина (карта не загрузилась) — город выбирает человек, и
 * он обязан быть действующей строкой map_cities. Анкета из старой вкладки
 * (до 14.10.2026 форма city не слала, а район был только бакинский) — Баку.
 * Онлайн — без города.
 */
export function resolveCity(d: CityInput, cities: readonly MapCity[]): string | null {
  if (d.address_mode === "online") return null;
  if (d.address_mode === "building" && d.point) return placeByPin(d.point.lat, d.point.lng, cities).city;
  if (d.city) return isActiveCity(cities, d.city) ? d.city : null;
  return d.district || d.travel_districts.length ? BAKU_CITY : null;
}

/**
 * Правила анкеты, которым нужен город, — после разбора, со справочником.
 * Возвращает имя нарушенного правила (его видно в Sentry) или null.
 * Районы вне Баку не отказ: их просто не пишем (partnerFieldsRow).
 */
export function cityProblem(d: PartnerFieldsData, cities: readonly MapCity[]): string | null {
  const city = resolveCity(d, cities);
  if (d.address_mode !== "online" && city === null) return "city required";
  if (city !== BAKU_CITY) return null;
  // Баку — как раньше: район у здания, районы у выезда.
  if (d.address_mode === "building" && d.district === null) return "district required";
  if (travelDistrictsMissing({ kind: d.kind, mode: d.address_mode, alsoTravel: d.also_travel, districts: d.travel_districts })) return "travel needs districts";
  return null;
}

/**
 * Поля анкеты — общие у новой анкеты (SubmitInput) и у правки по ссылке из
 * кабинета (partner-cabinet.ts → CabinetInput). Правила между полями — partnerRules.
 */
export const PartnerFields = z.object({
    kind: z.enum(["tutor", "club"]),
    name: z.string().trim().min(2).max(160),
    // Пусто можно, если назван «другой предмет» (subject_other) — см. partnerRules.
    subjects: z.array(z.string()).max(45),
    // «Digər / Другое / Other»: предмета нет в справочнике (20261031120000). Только для /admin.
    subject_other: z.string().trim().max(80).optional(),
    // Ориентир рядом с адресом (20261031120000). Показывается в профиле.
    landmark: z.string().trim().max(120).optional(),
    price: z.number().min(0).max(100000).nullable(),
    // Формат цены (PR B, 20261027120000). Нет поля — анкета из старой вкладки:
    // единица по виду (урок у репетитора, месяц у кружка), формат не подтверждён.
    price_unit: z.enum(["lesson", "month", "package"]).optional(),
    lessons_per_unit: z.number().int().min(1).max(100).nullable().optional(),
    lesson_minutes: z.number().int().min(10).max(300).nullable().optional(),
    formats: z.array(z.string().refine(isFormat)).max(4),
    trial_free: z.boolean(),
    grades: z.array(z.string().refine(isGrade)).max(4),
    age_min: z.number().int().min(1).max(99).nullable(),
    age_max: z.number().int().min(1).max(99).nullable(),
    address_mode: z.enum(["building", "travel", "online"]),
    point: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).nullable(),
    // Район Баку или посёлок Абшерона (src/lib/map-places.ts); подходит ли он городу — partnerFieldsRow.
    district: z.string().refine((d) => isDistrict(d) || ABSHERON_PLACES.some((x) => x.code === d)).nullable(),
    // Метро, выбранное вручную («Dəyiş», 26.10.2026): код станции или null — «метро нет».
    // Поля нет (старая вкладка) — метро по пину, не дальше 1,5 км.
    metro: z.string().refine(isMetro).nullable().optional(),
    travel_districts: z.array(z.string().refine(isDistrict)).max(12),
    // «Моё здание» + «Также могу приезжать к ученику»: районы выезда — в те же
    // travel_districts, address_mode остаётся building. Без галочки — пусто.
    also_travel: z.boolean().optional().default(false),
    // Кабинет: адрес не трогали — место берётся из анкеты, а не из этих полей (partner-cabinet.ts).
    keep_address: z.boolean().optional(),
    // Город, выбранный в форме (выезд; здание без пина). Код строки map_cities —
    // сверяет resolveCity. У здания с пином сервер считает город сам.
    city: z.string().regex(/^[a-z_]{2,40}$/).nullable().optional(),
    phone: Phone,
    // Только ник (src/lib/instagram.ts): «@ник» и ссылка приводятся к нику, имя с пробелом — отказ.
    instagram: z.string().trim().max(100).optional(),
    // Почта обязательна (14.10.2026): на неё приходят результат проверки и ссылка на правку.
    // Одна нормализация и проверка с формой (email-normalize.ts): невидимые символы, trim, строчные.
    email: EmailField,
    about: z.string().trim().max(1000).optional(),
    // Только если карта не загрузилась и пин поставить было нельзя.
    address_text: z.string().trim().max(300).optional(),
    lang: z.enum(["az", "ru", "en"]),
    // «Язык уроков» (20261118120000, src/lib/teach-langs.ts): несколько из AZ/RU/EN.
    // Нет поля (старая вкладка) — в кабинете языки анкеты не трогаем.
    teach_langs: z.array(z.enum(["az", "ru", "en"])).max(3).optional(),
    consent: z.literal(true),
});

export type PartnerFieldsData = z.infer<typeof PartnerFields>;

/** Правила между полями анкеты. Сообщение отказа — имя правила (его видно в Sentry). */
export function partnerRules(d: PartnerFieldsData, ctx: z.RefinementCtx) {
  const rule = (ok: boolean, message: string) => {
    if (!ok) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  };
  rule(d.subjects.every((s) => isSubjectOf(d.kind, s)), "unknown subject");
  // Хотя бы один предмет — из справочника или «другой».
  rule(d.subjects.length > 0 || !!d.subject_other?.trim(), "subjects");
  rule(d.age_min === null || d.age_max === null || d.age_min <= d.age_max, "age range");
  // Кружку — только точный пин на здании (решение владельца 06.10.2026).
  rule(d.kind === "tutor" || d.address_mode === "building", "club is building only");
  // Пин на здании обязателен (решение владельца 07.10.2026); без него — только
  // адрес текстом, когда карта у человека не загрузилась.
  rule(d.address_mode !== "building" || d.point !== null || !!d.address_text, "pin required");
  // Пин — в пределах страны (вся страна, не только Баку — 14.10.2026).
  rule(d.point === null || inAzerbaijan(d.point.lat, d.point.lng), "pin outside Azerbaijan");
  // Город и бакинские районы — cityProblem: ему нужен справочник map_cities.
  rule(normalizeInstagram(d.instagram) !== null, "instagram");
  // У пакета без числа уроков цену не сравнить.
  rule(d.price_unit !== "package" || !!d.lessons_per_unit, "package needs lessons");
}

/** Экспорт — для scripts/verify-map-homework.mts, verify-map-building-travel.mts и verify-tutor-join-ux.mts. */
export const SubmitInput = PartnerFields.extend({
  // Фото по желанию: base64 квадрата, обрезанного в браузере. Тип и размер
  // сервер проверяет сам (decodePhoto), галочка обязательна при файле.
  photo: z.string().max(MAP_PHOTO_MAX_B64).optional(),
  photo_consent: z.boolean().optional(),
  // Код приглашения коллеги (?ref= в адресе формы). Неверный — не отказ: его просто нет.
  ref: z.string().max(40).optional(),
  // Откуда пришла анкета (?src= в адресе формы). Неверный — не отказ: источника просто нет.
  source: z.string().max(80).optional(),
  // Капча не прошла и после повтора (встроенный браузер Facebook, 600010) — токена нет,
  // анкета всё равно принимается с пометкой captcha_failed (см. обработчик).
  turnstile_token: z.string().max(2048).optional(),
  // utm с первого захода на сайт (src/lib/utm.ts). Странное значение — не отказ: поле просто пустое.
  utm_source: z.unknown().optional(),
  utm_campaign: z.unknown().optional(),
}).superRefine(partnerRules);

/** Колонки миграции 20261031120000. */
export const JOIN_FIELD_COLUMNS = ["landmark", "subject_other", "source"] as const;

/** ?src= из адреса формы: латиница, цифры, дефис, подчёркивание, до 40 знаков, строчными. Иначе — null. */
export function normalizeSource(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim().toLowerCase();
  return /^[a-z0-9_-]{1,40}$/.test(v) ? v : null;
}

/**
 * Пригласивший по коду из формы (20261030120000). Нет кода, нет такой анкеты,
 * та же почта или телефон, колонки ещё нет — null, анкета пишется без него.
 */
async function resolveReferrer(raw: string | undefined, data: { email: string; phone: string }): Promise<string | null> {
  const code = normalizeRef(raw);
  if (!code) return null;
  const { data: owner, error } = await partners().select("id, email, phone").eq("ref_code", code).maybeSingle();
  if (error) {
    if (!isMissingColumn(error, "ref_code")) await reportSubmitFailure("ref_lookup", new Error(error.message), { ref: code });
    return null;
  }
  return referrerFor(owner ?? null, data);
}

/**
 * Колонки map_partners из полей анкеты — у новой анкеты и при «Принять» правки.
 * Без id, slug, lang, status, consent_at и фото: их ставит вызывающий.
 */
export function partnerFieldsRow(data: PartnerFieldsData, cities: readonly MapCity[]) {
  const onMap = data.address_mode === "building";
  const point = onMap ? data.point : null;
  const city = resolveCity(data, cities);
  const baku = city === BAKU_CITY;
  // Район и метро по пину (26.10.2026). Ручной выбор из формы важнее, если код
  // подходит городу пина; иначе — по границам и «метро не дальше 1,5 км».
  const place = point ? placeByPin(point.lat, point.lng, cities) : null;
  const district = place ? (isDistrictOf(city, data.district) ? data.district : place.district) : baku ? data.district : null;
  // Метро — только у Баку и Абшерона; присланное вручную вне их не пишется.
  const metro = place && (baku || city === ABSHERON_CITY) ? (data.metro === undefined ? place.metro : data.metro) : null;
  return {
    kind: data.kind,
    name: data.name,
    subjects: data.subjects,
    categories: [...new Set(data.subjects.map((s) => categoryOf(data.kind, s)!))],
    price_azn: data.price,
    price_unit: data.price_unit ?? (data.kind === "tutor" ? "lesson" : "month"),
    // Число уроков — у месяца (по желанию) и пакета. У урока его нет.
    lessons_per_unit: data.price_unit === "month" || data.price_unit === "package" ? (data.lessons_per_unit ?? null) : null,
    lesson_minutes: data.price_unit ? (data.lesson_minutes ?? null) : null,
    formats: data.formats,
    trial_free: data.trial_free,
    // Классы — у репетитора, возраст — у кружка.
    grades: data.kind === "tutor" ? data.grades : [],
    age_min: data.kind === "club" ? data.age_min : null,
    age_max: data.kind === "club" ? data.age_max : null,
    address_mode: data.address_mode,
    lat: point?.lat ?? null,
    lng: point?.lng ?? null,
    city,
    district,
    travel_districts: baku ? travelDistrictsFor({ kind: data.kind, mode: data.address_mode, alsoTravel: data.also_travel, districts: data.travel_districts }) : [],
    // Вне Баку и Абшерона районов и метро нет — что бы ни прислала форма.
    metro: city === BAKU_CITY || city === "absheron" ? metro : null,
    // Всегда +994XXXXXXXXX (24.10.2026, src/lib/phone-az.ts).
    phone: normalizePhoneAz(data.phone),
    whatsapp: normalizePhoneAz(data.phone),
    instagram: normalizeInstagram(data.instagram) || null,
    email: data.email.toLowerCase(),
    about: [data.address_text && !point ? `${ADDRESS_TEXT_PREFIX}${data.address_text}` : "", data.about ?? ""].filter(Boolean).join("\n") || null,
    // Онлайн-анкете ориентир не нужен: адреса нет.
    landmark: data.address_mode !== "online" && data.landmark?.trim() ? data.landmark.trim() : null,
    subject_other: data.subject_other?.trim() || null,
  };
}

/** Колонки миграции 20261027120000 (формат цены). */
export const PRICE_FORMAT_COLUMNS = ["lessons_per_unit", "lesson_minutes", "price_confirmed_at"] as const;

/** Ответ PostgREST до наката 20261027120000: нет колонки формата или package не проходит старый check. */
export function isPriceFormatSchemaError(error: { code?: string | null; message?: string | null } | null | undefined): boolean {
  if (!error) return false;
  if (PRICE_FORMAT_COLUMNS.some((c) => isMissingColumn(error, c))) return true;
  return error.code === "23514" && /price_unit/.test(error.message ?? "");
}

/** Формат цены выбран в форме (единица и сумма) — время подтверждения, иначе null. */
export const priceConfirmedNow = (d: Pick<PartnerFieldsData, "price" | "price_unit">) => (d.price_unit && d.price !== null ? new Date().toISOString() : null);

/** Адрес текстом (карта не загрузилась) пишется первой строкой «О себе» — правка его оттуда достаёт. */
export const ADDRESS_TEXT_PREFIX = "Адрес (без пина): ";

// slug анкеты — slugify из src/lib/partner-slug.ts (транслит до toLowerCase, хвост из id).

/**
 * Сбой отправки анкеты — в Sentry. Без данных анкеты: только этап и поля.
 * Отказ проверки — не сбой: warning, а не error (почту и прочие значения не шлём).
 */
async function reportSubmitFailure(stage: string, e: unknown, extra: Record<string, string> = {}) {
  try {
    const Sentry = await import("@sentry/cloudflare");
    // form_health — для правила оповещения Sentry о поломке /tutors/join (src/lib/form-health.ts).
    const health = joinStageHealth(stage);
    Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
      level: stage === "validation" ? "warning" : "error",
      tags: { map_partner_submit: stage, ...(health ? formHealthTags(health, "join") : {}) },
      extra,
    });
  } catch {
    /* no-op без DSN */
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const photoStorage = (): PhotoStorage => {
  const b = supabaseAdmin.storage.from(MAP_PHOTO_BUCKET);
  return {
    // Кэш CDN хранилища — минута, а не час по умолчанию (26.10.2026): фото,
    // заменённое админом поверх (тот же <id>.webp), иначе отдавалось старым.
    upload: async (key, body, opts) => ({ error: (await b.upload(key, body, { ...opts, cacheControl: "60" })).error }),
    remove: async (keys) => ({ error: (await b.remove(keys)).error }),
  };
};
const photoColumn = (): PhotoColumn => ({
  set: async (id, value) => ({ error: (await partners().update({ photo_url: value, updated_at: new Date().toISOString() }).eq("id", id)).error }),
  get: async (id) => {
    const { data, error } = await partners().select("photo_url").eq("id", id).maybeSingle();
    return { value: (data as any)?.photo_url ?? null, found: !!data, error };
  },
});
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Ссылка кнопки «Мой кабинет» для письма (многоразовая, partner-cabinet.ts).
 * Не выдалась — null: письмо уходит с прежней строкой «войти по почте».
 */
async function mailLinkOrNull(partnerId: string): Promise<string | null> {
  try {
    const [{ store }, { issueMailLink }] = await Promise.all([import("@/lib/partner-cabinet-store.server"), import("@/lib/partner-cabinet")]);
    return await issueMailLink(store, partnerId);
  } catch (e) {
    console.error("[map-partners] cabinet mail link", partnerId, e instanceof Error ? e.message : e);
    return null;
  }
}

/** Анкет без пройденной капчи в час с одного адреса (решение владельца 24.10.2026). */
export const CAPTCHA_FAILED_PER_HOUR = 3;

/** Сколько анкет без капчи пришло с этого адреса с sinceIso. Сбой чтения — 0: анкету не теряем. */
async function captchaFailedSince(ipHash: string, sinceIso: string): Promise<number> {
  const r = await partners().select("id", { count: "exact", head: true }).eq("captcha_failed", true).eq("ip_hash", ipHash).gte("created_at", sinceIso);
  if (r.error) {
    console.error("[map-partners] captcha limit read", r.error.message);
    return 0;
  }
  return r.count ?? 0;
}

async function sendSafely(payload: Parameters<typeof sendResendEmail>[0], what: string, id: string) {
  // Письмо — поверх записи: его потеря анкету не отменяет, но и не проходит
  // молча — событие в Sentry.
  try {
    const apiKey = process.env.RESEND_API_KEY;
    if (!apiKey) throw new Error("RESEND_API_KEY missing");
    await sendResendEmail(payload, { apiKey });
  } catch (e) {
    try {
      // Лениво: этот файл импортирует и страница, SDK воркера ей не нужен.
      const Sentry = await import("@sentry/cloudflare");
      Sentry.captureException(e, { tags: { map_partner_email: what }, extra: { partner_id: id } });
    } catch {
      /* no-op без DSN */
    }
  }
}

/**
 * Есть ли анкета с тем же телефоном (phone/whatsapp) или почтой — в любом
 * статусе (partner-duplicate.ts). Сбой чтения анкету не отменяет: дубль лучше
 * потерянной анкеты, но сбой уходит в Sentry, а не молчит.
 */
async function duplicateOf(probe: { phone: string; email: string }): Promise<DuplicateMatch> {
  const { rows, error } = await readAll<PartnerContactRow>(supabaseAdmin, "map_partners", "phone, whatsapp, email, status, deleted_by_owner_at, review_rejected_at", "id");
  if (error) {
    await reportSubmitFailure("duplicate_check", new Error(error.message));
    return null;
  }
  return findDuplicate(rows, probe);
}

export const submitMapPartner = createServerFn({ method: "POST" })
  // Разбор — в обработчике, а не в inputValidator: так отказ проверки виден в
  // Sentry (какие поля), а не теряется до кода сервера. Анкета 07.10 ~21:40
  // не дошла до базы, и следа не осталось ни одного.
  .inputValidator((input: unknown) => input)
  .handler(async ({ data: raw }) => {
    const parsed = SubmitInput.safeParse(raw);
    if (!parsed.success) {
      const fields = [...new Set(parsed.error.issues.map((i) => i.path.join(".") || i.message))].join(",");
      await reportSubmitFailure("validation", new Error(`map partner validation: ${fields}`), { fields });
      // Поле почты — форма покажет ошибку у поля, введённое не пропадёт.
      throw new Error(fields.split(",").includes("email") ? "validation:email" : "validation");
    }
    const data = parsed.data;
    // Фото проверяем ДО капчи и записи: нет галочки, неверный тип или размер —
    // отказ целиком, человек видит причину и может отправить без фото.
    const photo = decodePhoto(data.photo, data.photo_consent);
    if (photo && !photo.ok) {
      await reportSubmitFailure("photo_check", new Error(`map partner photo: ${photo.reason}`), { reason: photo.reason });
      throw new Error(photo.reason);
    }
    // КАПЧА НЕ ОТСЕКАЕТ АНКЕТУ (24.10.2026). Во встроенном браузере Facebook
    // на Android виджет падает с 600010, и заявки репетиторов из наших групп
    // терялись. Не прошла — анкета принимается с captcha_failed = true (видно
    // в /admin), все анкеты и так проверяются руками. Таких — не больше
    // CAPTCHA_FAILED_PER_HOUR в час с одного адреса. Секрета нет в проде —
    // по-прежнему отказ: это ошибка выкатки, а не человека.
    let captchaFailed: { ip_hash: string | null } | null = null;
    {
      let verdict: Awaited<ReturnType<typeof verifyTurnstile>>;
      try {
        verdict = await verifyTurnstile(data.turnstile_token);
      } catch (e) {
        verdict = { ok: false, reason: "unavailable" };
        await reportSubmitFailure("turnstile_threw", e);
      }
      if (!verdict.ok) {
        if (verdict.reason === "missing_secret_prod") {
          await reportSubmitFailure("turnstile", new Error("missing_secret_prod"));
          assertTurnstileOk(verdict);
        }
        const ipHash = await hashIp(callerIp());
        if (ipHash && (await captchaFailedSince(ipHash, new Date(Date.now() - 60 * 60 * 1000).toISOString())) >= CAPTCHA_FAILED_PER_HOUR) {
          await reportSubmitFailure("turnstile_rate_limited", new Error(`captcha failed, limit ${CAPTCHA_FAILED_PER_HOUR}/h`), { reason: verdict.reason });
          throw new Error("rate_limited");
        }
        await reportSubmitFailure("turnstile_bypassed", new Error(`captcha ${verdict.reason}`), { reason: verdict.reason, codes: (verdict.codes ?? []).join(",") });
        captchaFailed = { ip_hash: ipHash };
      }
    }

    // Справочник городов — до капчи не нужен, до записи обязателен.
    const cities = await loadMapCities();
    const cityRule = cityProblem(data, cities);
    if (cityRule) {
      await reportSubmitFailure("validation", new Error(`map partner validation: ${cityRule}`), { fields: cityRule });
      throw new Error("validation");
    }

    // ВТОРАЯ АНКЕТА НЕ СОЗДАЁТСЯ (19.10.2026): тот же телефон или почта — в
    // кабинет (есть почта) или в WhatsApp поддержки (почты нет).
    const dup = await duplicateOf({ phone: data.phone, email: data.email });
    if (dup) throw new Error((dup.canSignIn ? DUPLICATE_ERROR.cabinet : DUPLICATE_ERROR.whatsapp) + (dup.hidden ? DUPLICATE_ERROR.hidden : "") + (dup.rejected ? DUPLICATE_ERROR.rejected : ""));

    const id = crypto.randomUUID();
    const fields = partnerFieldsRow(data, cities);
    const { city } = fields;
    const referredBy = await resolveReferrer(data.ref, data);
    const row = {
      id,
      ...fields,
      ...(referredBy ? { referred_by_partner_id: referredBy } : {}),
      source: normalizeSource(data.source),
      // Формат цены выбран в форме — подтверждён самим репетитором.
      price_confirmed_at: priceConfirmedNow(data),
      slug: slugify(data.name, id),
      lang: data.lang,
      status: "pending",
      consent_at: new Date().toISOString(),
      ...(captchaFailed ? { captcha_failed: true, ip_hash: captchaFailed.ip_hash } : {}),
      // utm_source / utm_campaign с первого захода на сайт (src/lib/utm.ts), только если были.
      ...utmColumns(data),
      // Языки уроков — только если отмечены: без них подбор берёт lang (src/lib/teach-langs.ts).
      ...(normalizeTeachLangs(data.teach_langs) ? { teach_langs: normalizeTeachLangs(data.teach_langs) } : {}),
    };
    let { error } = await partners().insert(row);
    // До наката 20261118120000 колонки языков уроков нет: анкету не теряем, пишем без неё.
    if (error && isMissingColumn(error, TEACH_LANGS_COLUMN)) {
      await reportSubmitFailure("insert_without_teach_langs", new Error(error.message), { partner_id: id });
      // Из самой row: следующие запасные вставки копируют её.
      delete (row as Record<string, unknown>)[TEACH_LANGS_COLUMN];
      ({ error } = await partners().insert(row));
    }
    // До наката 20261111120000 колонок utm нет: анкету не теряем, пишем без них.
    if (error && UTM_COLUMNS.some((c) => isMissingColumn(error, c))) {
      await reportSubmitFailure("insert_without_utm", new Error(error.message), { partner_id: id });
      // Из самой row: следующие запасные вставки копируют её и не должны вернуть utm.
      for (const c of UTM_COLUMNS) delete (row as Record<string, unknown>)[c];
      ({ error } = await partners().insert(row));
    }
    // До наката 20261108120000 колонок капчи нет: анкету не теряем, пишем без пометки.
    if (error && captchaFailed && (isMissingColumn(error, "captcha_failed") || isMissingColumn(error, "ip_hash"))) {
      await reportSubmitFailure("insert_without_captcha_flag", new Error(error.message), { partner_id: id });
      const old: Record<string, unknown> = { ...row };
      delete old.captcha_failed;
      delete old.ip_hash;
      ({ error } = await partners().insert(old));
    }
    // До наката 20261031120000 нет ориентира, «другого предмета» и источника: пишем без них.
    if (error && JOIN_FIELD_COLUMNS.some((c) => isMissingColumn(error, c))) {
      await reportSubmitFailure("insert_without_join_fields", new Error(error.message), { partner_id: id });
      const old: Record<string, unknown> = { ...row };
      for (const c of JOIN_FIELD_COLUMNS) delete old[c];
      ({ error } = await partners().insert(old));
    }
    // До наката 20261030120000 колонки пригласившего нет: анкету не теряем, пишем без неё.
    if (error && isMissingColumn(error, "referred_by_partner_id")) {
      await reportSubmitFailure("insert_without_referrer", new Error(error.message), { partner_id: id });
      const withoutRef: Record<string, unknown> = { ...row };
      delete withoutRef.referred_by_partner_id;
      ({ error } = await partners().insert(withoutRef));
    }
    // До наката 20261025120000 колонки city нет. Анкету не теряем: пишем без
    // города и сообщаем в Sentry (город виден в письме 3c, его можно проставить).
    if (error && isMissingColumn(error, "city")) {
      await reportSubmitFailure("insert_without_city", new Error(error.message), { city: city ?? "" });
      const withoutCity: Partial<typeof row> = { ...row };
      delete withoutCity.city;
      ({ error } = await partners().insert(withoutCity));
    }
    // До наката 20261027120000 нет колонок формата цены, а price_unit = package
    // не проходит старое ограничение. Анкету не теряем: пишем по-старому
    // (единица по виду, без подтверждения) и сообщаем в Sentry.
    if (error && isPriceFormatSchemaError(error)) {
      await reportSubmitFailure("insert_without_price_format", new Error(error.message), { unit: String(data.price_unit ?? "") });
      const old: Record<string, unknown> = { ...row, price_unit: data.kind === "tutor" ? "lesson" : "month" };
      for (const k of PRICE_FORMAT_COLUMNS) delete old[k];
      ({ error } = await partners().insert(old));
    }
    if (error) {
      await reportSubmitFailure("insert", new Error(error.message), { code: String(error.code ?? "") });
      throw new Error(error.message);
    }

    // Фото — поверх записи: его сбой анкету не отменяет. В Sentry — этап и id,
    // человеку — честное «анкета дошла, фото нет».
    let photoStatus: "none" | "saved" | "failed" = "none";
    if (photo?.ok) {
      const r = await savePartnerPhoto({ storage: photoStorage(), column: photoColumn() }, id, photo);
      photoStatus = r.status;
      if (r.status === "failed") await reportSubmitFailure(`photo_${r.stage}`, new Error(r.error), { partner_id: id });
    }

    // Этот браузер сразу в кабинете (24.10.2026): репетитор не должен «входить».
    // Сессия — поверх записи: её сбой анкету не отменяет, вход по почте остаётся.
    try {
      const [{ store }, { startSession }, { setSessionCookie }] = await Promise.all([
        import("@/lib/partner-cabinet-store.server"),
        import("@/lib/partner-cabinet"),
        import("@/lib/partner-cabinet-cookie.server"),
      ]);
      setSessionCookie(await startSession(store, id, null));
    } catch (e) {
      await reportSubmitFailure("session", e instanceof Error ? e : new Error(String(e)), { partner_id: id });
    }

    if (row.email) {
      const m = receivedEmail(data.lang, data.name, await mailLinkOrNull(id));
      await sendSafely(
        { from: FROM_ADDRESS, to: row.email, reply_to: SUPPORT, subject: m.subject, html: m.html, text: m.text, tags: [{ name: "kind", value: "map_partner_received" }], idempotency_key: `map-partner-received-${id}` },
        "received",
        id,
      );
    }
    // Нам — строка в ленте «Уведомления» поверх записи: её сбой анкету не
    // отменяет (notifyAdmin ошибку не бросает). Писем владельцу о модерации
    // нет (25.10.2026, лимит Resend): новые видны в /admin — «Ждут проверки: N»
    // и бейдж непросмотренных на вкладке «Анкеты» (admin_viewed_at).
    await notifyAdmin({
      kind: "feedback_submitted",
      title: feedTitle({ kind: data.kind, name: data.name, subjects: data.subjects }),
      metadata: { kind: "map_partner_new", partner_id: id, partner_kind: data.kind },
    });
    // Код приглашения новой анкеты — для экрана «Спасибо» («Пригласите коллег»).
    // Его ставит default базы; до наката 20261030120000 кода нет — блок не показывается.
    const { data: own } = await partners().select("ref_code").eq("id", id).maybeSingle();
    const refCode = normalizeRef(own?.ref_code);
    return { ok: true as const, photo: photoStatus, ref_code: refCode };
  });

// ─── Модерация: вкладка «Анкеты» в /admin ───────────────────────────
/* eslint-disable @typescript-eslint/no-explicit-any */
async function ensureAdmin(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
  if (!data) throw new Error("Forbidden: admin role required");
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export type AdminMapPartner = {
  id: string;
  kind: "tutor" | "club";
  name: string;
  slug: string;
  subjects: string[];
  price_azn: number | null;
  price_unit: "lesson" | "month" | "package";
  /** Формат цены (20261027120000). undefined — до наката. */
  lessons_per_unit?: number | null;
  lesson_minutes?: number | null;
  price_confirmed_at?: string | null;
  address_mode: string;
  /** Код города (map_cities) и его подпись по-русски — колонка «Город» в /admin. */
  city: string | null;
  city_label: string | null;
  district: string | null;
  /** Районы выезда — для визитки в /admin. */
  travel_districts: string[];
  metro: string | null;
  phone: string;
  email: string | null;
  instagram: string | null;
  lang: "az" | "ru" | "en";
  status: "pending" | "published" | "hidden";
  created_at: string;
  /**
   * Когда админ отметил анкету просмотренной. null — новая, непросмотренная.
   * undefined — колонки ещё нет (миграция 20261017120000 не накатана).
   */
  admin_viewed_at?: string | null;
  /** Пауза и удаление из кабинета (20261026120000). undefined — до наката. */
  paused_at?: string | null;
  pause_reason?: "full" | "vacation" | null;
  paused_until?: string | null;
  deleted_by_owner_at?: string | null;
  /** Принята без пройденной капчи (сбой Turnstile, чаще встроенный браузер Facebook). */
  captcha_failed?: boolean;
  /** Новая анкета отклонена на /admin/review и ждёт исправления в кабинете. */
  review_rejected?: boolean;
  /** Подписанная ссылка на 10 минут — только для админки, бакет приватный. */
  photo_preview: string | null;
  /** Кто пригласил (20261030120000). null — сам или до наката. */
  referred_by_partner_id: string | null;
  /** «Digər», источник (?src=) и ориентир (20261031120000). null — нет или до наката. */
  subject_other: string | null;
  source: string | null;
  landmark: string | null;
};

const CABINET_ADMIN_COLUMNS = "paused_at, pause_reason, paused_until, deleted_by_owner_at";
const ADMIN_COLUMNS = "id, kind, name, slug, subjects, price_azn, price_unit, address_mode, city, district, travel_districts, metro, phone, email, instagram, lang, status, created_at, photo_url";

export const listMapPartners = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const read = (cols: string) => partners().select(cols).order("created_at", { ascending: false }).limit(500);
    // Fail-open до наката: колонки admin_viewed_at нет → читаем без неё,
    // панель работает как раньше, только без пометок «новая».
    // Пометки «на паузе» и «удалена владельцем» — колонки кабинета; до их наката — без них.
    // Формат цены (20261027120000) — сначала с ним; до наката — без него.
    let { data, error } = await read(`${ADMIN_COLUMNS}, admin_viewed_at, ${CABINET_ADMIN_COLUMNS}, ${PRICE_FORMAT_COLUMNS.join(", ")}`);
    if (error && isPriceFormatSchemaError(error)) {
      ({ data, error } = await read(`${ADMIN_COLUMNS}, admin_viewed_at, ${CABINET_ADMIN_COLUMNS}`));
    }
    if (error && (isMissingColumn(error, "paused_at") || isMissingColumn(error, "deleted_by_owner_at") || isMissingColumn(error, "pause_reason"))) {
      ({ data, error } = await read(`${ADMIN_COLUMNS}, admin_viewed_at`));
    }
    let viewedSupported = true;
    if (error && isMissingViewedColumn(error)) {
      viewedSupported = false;
      ({ data, error } = await read(ADMIN_COLUMNS));
    }
    if (error) throw new Error(error.message);
    const raw = (data ?? []) as (Omit<AdminMapPartner, "photo_preview" | "city_label" | "referred_by_partner_id" | "subject_other" | "source" | "landmark"> & { photo_url: string | null })[];
    // Фото видно админу и до публикации — подписанной ссылкой на 10 минут.
    const keys = raw.map((r) => objectKeyFromStored(r.photo_url)).filter((k): k is string => !!k);
    const signed = new Map<string, string>();
    if (keys.length) {
      const { data: urls, error: signErr } = await supabaseAdmin.storage.from(MAP_PHOTO_BUCKET).createSignedUrls(keys, 600);
      if (signErr) console.error("[map-partners] createSignedUrls", signErr.message);
      for (const u of urls ?? []) if (u.path && u.signedUrl) signed.set(u.path, u.signedUrl);
    }
    const cities = await loadMapCities();
    // Кто кого пригласил — отдельным запросом: до наката 20261030120000 колонки нет, панель работает без неё.
    const referredBy = new Map<string, string>();
    {
      const r = await partners().select("id, referred_by_partner_id").not("referred_by_partner_id", "is", null).limit(2000);
      if (r.error && !isMissingColumn(r.error, "referred_by_partner_id")) console.error("[map-partners] referred_by", r.error.message);
      for (const x of (r.data ?? []) as { id: string; referred_by_partner_id: string }[]) referredBy.set(x.id, x.referred_by_partner_id);
    }
    // «Digər», источник и ориентир — тоже отдельным запросом: до наката 20261031120000 колонок нет.
    const joinFields = new Map<string, { subject_other: string | null; source: string | null; landmark: string | null }>();
    {
      const r = await partners().select("id, subject_other, source, landmark").limit(2000);
      if (r.error && !JOIN_FIELD_COLUMNS.some((c) => isMissingColumn(r.error, c))) console.error("[map-partners] join fields", r.error.message);
      for (const x of (r.data ?? []) as { id: string; subject_other: string | null; source: string | null; landmark: string | null }[]) joinFields.set(x.id, x);
    }
    // Принята без капчи (20261108120000) — отдельным запросом: до наката колонки нет, панель работает без пометки.
    const captchaFailed = new Set<string>();
    {
      const r = await partners().select("id").eq("captcha_failed", true).limit(2000);
      if (r.error && !isMissingColumn(r.error, "captcha_failed")) console.error("[map-partners] captcha_failed", r.error.message);
      for (const x of (r.data ?? []) as { id: string }[]) captchaFailed.add(x.id);
    }
    // Новая анкета отклонена и ждёт исправления в кабинете (20261110120000) — тоже отдельным запросом.
    const reviewRejected = new Set<string>();
    {
      const r = await partners().select("id").not("review_rejected_at", "is", null).limit(2000);
      if (r.error && !isMissingColumn(r.error, "review_rejected_at")) console.error("[map-partners] review_rejected_at", r.error.message);
      for (const x of (r.data ?? []) as { id: string }[]) reviewRejected.add(x.id);
    }
    const rows: AdminMapPartner[] = raw.map(({ photo_url, ...r }) => {
      const key = objectKeyFromStored(photo_url);
      return { ...r, captcha_failed: captchaFailed.has(r.id), review_rejected: reviewRejected.has(r.id), city_label: r.city ? cityLabel(cities, r.city, "ru") : null, photo_preview: key ? (signed.get(key) ?? null) : null, referred_by_partner_id: referredBy.get(r.id) ?? null, subject_other: joinFields.get(r.id)?.subject_other ?? null, source: joinFields.get(r.id)?.source ?? null, landmark: joinFields.get(r.id)?.landmark ?? null };
    });
    return {
      rows,
      pending: rows.filter((r) => r.status === "pending").length,
      unseen: viewedSupported ? countUnseen(rows) : null,
      districts: DISTRICTS,
    };
  });

/**
 * Бейдж на вкладке «Анкеты»: сколько анкет админ ещё не смотрел
 * (admin_viewed_at is null). Статус не важен: новая анкета всегда pending, а
 * «Опубликовать»/«Скрыть» и так ставят отметку. null — колонки нет (до
 * наката), бейдж не показывается, ошибки нет.
 */
export const countUnseenMapPartners = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    // Не head: у HEAD-ответа нет тела, и код 42703 до клиента не доходит.
    const { count, error } = await partners().select("id", { count: "exact" }).is("admin_viewed_at", null).limit(1);
    if (error) {
      if (isMissingViewedColumn(error)) return { unseen: null };
      throw new Error(error.message);
    }
    return { unseen: count ?? 0 };
  });

/** Ставит admin_viewed_at = now() у ещё не просмотренных. Колонки нет — тихо ничего. */
async function markViewed(ids: string[] | "all"): Promise<{ supported: boolean; error?: string }> {
  let q = partners().update({ admin_viewed_at: new Date().toISOString() }).is("admin_viewed_at", null);
  if (ids !== "all") q = q.in("id", ids);
  const { error } = await q;
  if (error) {
    if (isMissingViewedColumn(error)) return { supported: false };
    return { supported: true, error: error.message };
  }
  return { supported: true };
}

/** «Отметить просмотренной» / «Отметить все» / открытие анкеты по ссылке из письма. */
export const markMapPartnersViewed = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.union([z.object({ ids: z.array(z.string().uuid()).min(1).max(500) }), z.object({ all: z.literal(true) })]).parse(input),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const r = await markViewed("all" in data ? "all" : data.ids);
    if (r.error) throw new Error(r.error);
    return { ok: true, supported: r.supported };
  });

export const setMapPartnerStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid(), status: z.enum(["published", "hidden"]) }).parse(input))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { data: before, error: readErr } = await partners().select("name, slug, email, lang, status, ref_code").eq("id", data.id).maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!before) throw new Error("Анкета не найдена");
    const { error } = await partners().update({ status: data.status, updated_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw new Error(error.message);
    // Решение по анкете = анкету видели. Отдельным запросом: до наката колонки
    // его отказ не должен ломать публикацию, поэтому ошибка только в лог.
    const viewed = await markViewed([data.id]);
    if (viewed.error) console.error("[map-partners] mark viewed", viewed.error);
    // 3b — только при первой публикации (повторная после «Скрыть» не шлёт ещё раз).
    if (data.status === "published" && before.status === "pending" && before.email) {
      // У всех трёх языков свой префикс: русский профиль с 14.10.2026 — /ru/tutors/<slug>.
      // QR на профиль и приглашение коллеги по коду анкеты (ref_code; нет кода — строки нет).
      const ref = normalizeRef(before.ref_code);
      const m = publishedEmail(before.lang, before.name, `${SITE}/${before.lang}/tutors/${before.slug}`, {
        qrImageUrl: emailQrImageUrl(before.slug, before.lang),
        inviteUrl: ref ? inviteLink(before.lang, ref) : null,
        cabinetLink: await mailLinkOrNull(data.id),
      });
      await sendSafely(
        { from: FROM_ADDRESS, to: before.email, reply_to: SUPPORT, subject: m.subject, html: m.html, text: m.text, tags: [{ name: "kind", value: "map_partner_published" }], idempotency_key: `map-partner-published-${data.id}` },
        "published",
        data.id,
      );
    }
    return { ok: true };
  });

/**
 * «Загрузить фото» (26.10.2026): фото, присланное репетитором в WhatsApp,
 * кладёт админ. Браузер готовит квадрат так же, как анкета (prepareMapPhoto),
 * сервер проверяет его тем же decodePhoto. Согласие на публикацию — сам факт,
 * что репетитор прислал фото для анкеты. Только админ.
 */
export const uploadMapPartnerPhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid(), photo: z.string().min(1).max(MAP_PHOTO_MAX_B64) }).parse(input))
  .handler(async ({ data, context }) =>
    replacePartnerPhotoAsAdmin(
      {
        isAdmin: async () => {
          try {
            await ensureAdmin(context.supabase, context.userId);
            return true;
          } catch {
            return false;
          }
        },
        storage: photoStorage(),
        column: photoColumn(),
      },
      data.id,
      decodePhoto(data.photo, true),
    ),
  );

/** «Убрать фото»: анкета остаётся, файл — из бакета, photo_url = null. Только админ. */
export const removeMapPartnerPhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) =>
    removePartnerPhotoAsAdmin(
      {
        isAdmin: async () => {
          try {
            await ensureAdmin(context.supabase, context.userId);
            return true;
          } catch {
            return false;
          }
        },
        storage: photoStorage(),
        column: photoColumn(),
      },
      data.id,
    ),
  );
